/**
 * Smooth wheel scrolling on top of native document scroll (Lenis).
 * Touch stays fully native; keyboard, anchors and programmatic scrolls are
 * left to the browser and Lenis simply stays in sync with them.
 */
(() => {
  if (typeof Lenis === 'undefined' || window.Shopify?.designMode) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Touch-only devices scroll natively anyway; skip Lenis and its per-frame loop.
  if (!window.matchMedia('(any-pointer: fine)').matches) return;

  const lenis = new Lenis({
    lerp: 0.1,
    wheelMultiplier: 1,
    smoothWheel: true,
    syncTouch: false,
    // Drawers, menus, dropdowns and other overflow areas keep their own scroll
    allowNestedScroll: true,
    stopInertiaOnNavigate: true,
    autoRaf: true,
    // Horizontal-dominant gestures belong to carousels, the product desk and
    // the browser's swipe navigation, never to the page
    virtualScroll: ({ deltaX, deltaY }) => Math.abs(deltaY) > Math.abs(deltaX),
  });

  window.lenis = lenis;

  // A glide still settling would overwrite any other scroll on its next frame.
  // Hand control back to the browser whenever something else scrolls the page:
  // theme code (gallery, anchors, search), keyboard, or the scrollbar.
  let isLenisScroll = false;
  const setScroll = lenis.setScroll.bind(lenis);
  lenis.setScroll = (scroll) => {
    isLenisScroll = true;
    try {
      setScroll(scroll);
    } finally {
      isLenisScroll = false;
    }
  };

  const releaseGlide = () => {
    if (!isLenisScroll && lenis.isScrolling === 'smooth') lenis.reset();
  };

  for (const [target, method] of [
    [window, 'scrollTo'],
    [window, 'scroll'],
    [window, 'scrollBy'],
    [Element.prototype, 'scrollIntoView'],
  ]) {
    const original = target[method];
    target[method] = function (...args) {
      releaseGlide();
      return original.apply(this, args);
    };
  }

  const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
  window.addEventListener('keydown', (event) => {
    if (scrollKeys.has(event.key)) releaseGlide();
  });
  // Clicks on the classic scrollbar target the root element
  window.addEventListener('pointerdown', (event) => {
    if (event.target === document.documentElement) releaseGlide();
  });

  // Theme drawers and modals lock scroll with body.overflow-hidden* classes;
  // mirror that lock so wheel input cannot move the page underneath them.
  const syncLock = () => {
    const locked =
      getComputedStyle(document.body).overflowY === 'hidden' ||
      getComputedStyle(document.documentElement).overflowY === 'hidden';
    if (locked) lenis.stop();
    else lenis.start();
  };

  new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
  window.addEventListener('resize', syncLock, { passive: true });
  syncLock();
})();
